import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { submitContactForm } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const contactSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    subject: z.string().min(5, 'Subject must be at least 5 characters'),
    message: z.string().min(20, 'Message must be at least 20 characters')
});

const ContactPage = () => {
    const { t } = useLanguage();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    const faqItems = [
        {
            question: 'Başvuru son tarihi nedir?',
            answer: 'Başvurular 24 Mart tarihine kadar kabul edilmektedir. Yoğunluk oluşabileceği için erken başvuru yapmanızı tavsiye ederiz.'
        },
        {
            question: 'Kayıt ücreti var mı?',
            answer: 'Evet, konferans için kayıt ücreti bulunmaktadır. Detaylı bilgi almak için PR ekibimizle iletişime geçebilirsiniz:',
            phone: '0533 510 14 06'
        },
        {
            question: 'İlk kez delege olarak başvurabilir miyim?',
            answer: 'Elbette. Konferansımız ilk kez katılacak delegelere açıktır. Akademik ekibimiz süreç boyunca gerekli yönlendirmeleri sağlayacaktır.'
        },
        {
            question: 'Konferansa ne getirmeliyim?',
            answer: 'Konferans süresince ihtiyaç duyacağınız materyaller tarafımızdan temin edilecektir. Sadece kişisel notlarınız ve gerekli bireysel eşyalarınız yeterlidir.'
        }
    ];

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    const contactInfo = [
        {
            icon: Mail,
            title: t('contact.emailUs'),
            details: 'girnemun26@gmail.com',
            description: t('contact.forGeneral')
        },
        {
            icon: Phone,
            title: t('contact.callUs'),
            details: '+90 533 510 14 06',
            description: 'Monday - Friday, 9AM - 5PM'
        },
        {
            icon: MapPin,
            title: t('contact.visitUs'),
            details: 'Girne College Campus',
            description: 'Adana, Turkey'
        }
    ];

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        resolver: zodResolver(contactSchema)
    });

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            await submitContactForm(data);
            setSubmitSuccess(true);
            reset();
            setTimeout(() => setSubmitSuccess(false), 5000);
        } catch (error) {
            console.error('Error submitting contact form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Hero Section */}
            <section className="pt-32 pb-16 bg-primary">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-4">
                            {t('contact.subtitle')}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
                            {t('contact.title')}
                        </h1>
                        <p className="text-xl text-primary-foreground/80 leading-relaxed">
                            {t('contact.description')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Content */}
            <section className="py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Contact Info Cards */}
                        <div className="lg:col-span-1 space-y-6">
                            {contactInfo.map((info, index) => (
                                <div
                                    key={index}
                                    className="p-6 rounded-xl bg-card border border-border hover:border-accent/30 transition-all duration-300"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                                        <info.icon className="w-6 h-6 text-accent" />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-foreground mb-1">
                                        {info.title}
                                    </h3>
                                    <p className="text-foreground font-medium">{info.details}</p>
                                    <p className="text-muted-foreground text-sm mt-1">
                                        {info.description}
                                    </p>
                                </div>
                            ))}

                            {/* Social Links */}
                            <div className="p-6 rounded-xl bg-primary">
                                <h3 className="font-display text-lg font-semibold text-primary-foreground mb-4">
                                    {t('contact.followUs')}
                                </h3>
                                <p className="text-primary-foreground/70 text-sm mb-4">
                                    {t('contact.stayUpdated')}
                                </p>
                                <div className="flex gap-3">
                                    <a
                                        href="https://www.instagram.com/baran_topcil?igsh=NWE4ZHQ5bzRpbWhv&utm_source=qr"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 py-2 rounded-lg bg-primary-foreground/10 text-primary-foreground text-sm font-medium hover:bg-primary-foreground/20 transition-colors"
                                    >
                                        Instagram
                                    </a>
                                    <a
                                        href="https://www.tiktok.com/@girne.mun?_r=1&_t=ZS-94C1yQtT5Xx"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-4 py-2 rounded-lg bg-primary-foreground/10 text-primary-foreground text-sm font-medium hover:bg-primary-foreground/20 transition-colors"
                                    >
                                        TikTok
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <div className="bg-card rounded-xl border border-border p-8">
                                <h2 className="font-display text-2xl font-bold text-foreground mb-2">
                                    {t('contact.sendMessage')}
                                </h2>
                                <p className="text-muted-foreground mb-8">
                                    {t('contact.formDesc')}
                                </p>

                                {submitSuccess && (
                                    <div className="flex items-center gap-3 p-4 rounded-lg bg-success/10 border border-success/20 mb-6">
                                        <CheckCircle className="w-5 h-5 text-success shrink-0" />
                                        <p className="text-success">
                                            {t('contact.successMessage')}
                                        </p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* Name */}
                                        <div>
                                            <Label htmlFor="name" className="text-foreground font-medium">
                                                {t('contact.yourName')} <span className="text-destructive">*</span>
                                            </Label>
                                            <Input
                                                id="name"
                                                {...register('name')}
                                                className={cn(
                                                    "mt-2 input-elegant",
                                                    errors.name && "border-destructive"
                                                )}
                                                placeholder="John Doe"
                                            />
                                            {errors.name && (
                                                <p className="text-destructive text-sm mt-1">{errors.name.message}</p>
                                            )}
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <Label htmlFor="email" className="text-foreground font-medium">
                                                {t('contact.emailAddress')} <span className="text-destructive">*</span>
                                            </Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                {...register('email')}
                                                className={cn(
                                                    "mt-2 input-elegant",
                                                    errors.email && "border-destructive"
                                                )}
                                                placeholder="john@example.com"
                                            />
                                            {errors.email && (
                                                <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Subject */}
                                    <div>
                                        <Label htmlFor="subject" className="text-foreground font-medium">
                                            {t('contact.subject')} <span className="text-destructive">*</span>
                                        </Label>
                                        <Input
                                            id="subject"
                                            {...register('subject')}
                                            className={cn(
                                                "mt-2 input-elegant",
                                                errors.subject && "border-destructive"
                                            )}
                                            placeholder={t('contact.subjectPlaceholder')}
                                        />
                                        {errors.subject && (
                                            <p className="text-destructive text-sm mt-1">{errors.subject.message}</p>
                                        )}
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <Label htmlFor="message" className="text-foreground font-medium">
                                            {t('contact.message')} <span className="text-destructive">*</span>
                                        </Label>
                                        <Textarea
                                            id="message"
                                            {...register('message')}
                                            className={cn(
                                                "mt-2 input-elegant min-h-[150px]",
                                                errors.message && "border-destructive"
                                            )}
                                            placeholder={t('contact.messagePlaceholder')}
                                        />
                                        {errors.message && (
                                            <p className="text-destructive text-sm mt-1">{errors.message.message}</p>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="flex justify-end">
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="btn-premium px-8 py-6 text-base font-semibold rounded-lg text-primary hover:text-primary"
                                        >
                                            {isSubmitting ? (
                                                <span className="flex items-center gap-2">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    {t('contact.sending')}
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-2">
                                                    <Send className="w-5 h-5" />
                                                    {t('contact.send')}
                                                </span>
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 section-cream">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="font-display text-3xl font-bold text-foreground mb-4">
                            {t('contact.faq')}
                        </h2>
                        <p className="text-muted-foreground">
                            {t('contact.faqDesc')}
                        </p>
                    </div>
                    
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <div
                                key={index}
                                className={cn(
                                    "rounded-xl border border-border overflow-hidden transition-all duration-300",
                                    openFaqIndex === index 
                                        ? "bg-primary/5 border-accent/30 shadow-md" 
                                        : "bg-card hover:border-accent/20"
                                )}
                            >
                                {/* Question Header */}
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full flex items-center justify-between p-5 text-left"
                                >
                                    <span className={cn(
                                        "font-semibold text-base transition-colors duration-300",
                                        openFaqIndex === index ? "text-accent" : "text-foreground"
                                    )}>
                                        {item.question}
                                    </span>
                                    <ChevronDown 
                                        className={cn(
                                            "w-5 h-5 text-accent transition-transform duration-300 ease-out shrink-0 ml-4",
                                            openFaqIndex === index && "rotate-180"
                                        )}
                                    />
                                </button>
                                
                                {/* Answer Content */}
                                <div 
                                    className={cn(
                                        "overflow-hidden transition-all duration-300 ease-out",
                                        openFaqIndex === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                                    )}
                                >
                                    <div className="px-5 pb-5 pt-0">
                                        <div className="border-t border-border/50 pt-4">
                                            <p className="text-muted-foreground leading-relaxed">
                                                {item.answer}
                                            </p>
                                            {item.phone && (
                                                <a 
                                                    href={`tel:${item.phone.replace(/\s/g, '')}`}
                                                    className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-accent/10 text-accent font-semibold rounded-lg hover:bg-accent/20 transition-colors"
                                                >
                                                    <Phone className="w-4 h-4" />
                                                    {item.phone}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default ContactPage;
